import { drillStateToSearchParams, type DrillUrlState } from "./drillStateUrl";

export const EQUIPMENT_AI_BASE = "/products/equipment-ai";

// Standalone full page for per-slot ingestion config (storage only). Reached by
// typing the URL — intentionally not linked from any nav/tab.
export const EQUIPMENT_AI_INTEGRATIONS_PATH = `${EQUIPMENT_AI_BASE}/integrations`;

// Standalone Schedules page (scheduled analysis coverage), reached from the
// header's Schedules tab; it mounts its own component, like Integrations.
export const EQUIPMENT_AI_SCHEDULES_PATH = `${EQUIPMENT_AI_BASE}/schedules`;

// Standalone AF-XML import page. Like Integrations, it mounts its own component
// and is reached by URL only — intentionally NOT in EQUIPMENT_AI_ROUTES (which
// is the main pathname-driven surface) and not linked from any nav/tab.
export const onboardingUrl = (): string => `${EQUIPMENT_AI_BASE}/onboarding`;

/**
 * Match list for the main EquipmentAI surface, in match order — the single
 * source routes.jsx + tests mount that component from. The standalone
 * Integrations page (EQUIPMENT_AI_INTEGRATIONS_PATH) mounts its own component
 * and is intentionally excluded.
 */
export const EQUIPMENT_AI_ROUTES: string[] = [
  EQUIPMENT_AI_BASE,
  `${EQUIPMENT_AI_BASE}/location/:locationId`,
  // Alarms surface — same mounted EquipmentAI component, pathname-driven.
  // `${BASE}/alarms/plan` (the shared procedure screen) matches
  // :alarmDefinitionId and is disambiguated by pathname in EquipmentAI; alarm
  // chat resume rides the `?chat_id` query param like the rest of the surface.
  `${EQUIPMENT_AI_BASE}/alarms`,
  `${EQUIPMENT_AI_BASE}/alarms/:alarmDefinitionId`,
];

interface ChatOption {
  chatId?: number;
}

/** Parent chain carried onto a location URL so its breadcrumb renders instantly. */
export interface LocationParents {
  parentSite?: string;
  parentUnit?: string;
  parentSystem?: string;
  parentAf?: string;
}

interface LocationOption extends ChatOption {
  parents?: LocationParents;
}

/** Hierarchy page URL: drill position in the query string, optional chat. */
export const hierarchyUrl = (
  drill: DrillUrlState,
  opts: ChatOption = {},
): string => {
  const params = drillStateToSearchParams(drill);
  applyChatParam(params, opts.chatId);
  return withSearch(EQUIPMENT_AI_BASE, params);
};

/**
 * Location page URL: location id in the path (encoded once), optional chat, and
 * the optional parent chain (site/unit/system) carried forward from the
 * hierarchy so the breadcrumb renders its ancestry without an extra fetch.
 */
export const locationUrl = (
  locationId: string,
  opts: LocationOption = {},
): string => {
  const params = new URLSearchParams();
  applyParentParams(params, opts.parents);
  applyChatParam(params, opts.chatId);
  const path = `${EQUIPMENT_AI_BASE}/location/${encodeURIComponent(locationId)}`;
  return withSearch(path, params);
};

/** Set chat_id on an existing URL, preserving every other param. */
export const withChat = (url: string, chatId: number): string =>
  mutateSearch(url, (params) => params.set("chat_id", String(chatId)));

/** Strip chat_id from an existing URL, preserving every other param. */
export const withoutChat = (url: string): string =>
  mutateSearch(url, (params) => params.delete("chat_id"));

export const parseChatId = (raw: string | null): number | undefined => {
  if (!raw) return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * The risk band a chip click activates, mapped to a backend health band:
 * high→red (critical), medium→orange (watch), low→green (healthy). These are the
 * only values the `/report` endpoint accepts; `gray` (no score) has no chip.
 */
export type RiskBand = "high" | "medium" | "low";

const RISK_BANDS: readonly RiskBand[] = ["high", "medium", "low"];
const RISK_PARAM = "risk";

/** Set the active risk band on a URL, preserving every other param. */
export const withRisk = (url: string, risk: RiskBand): string =>
  mutateSearch(url, (params) => params.set(RISK_PARAM, risk));

/** Strip the risk band from a URL, preserving every other param. */
export const withoutRisk = (url: string): string =>
  mutateSearch(url, (params) => params.delete(RISK_PARAM));

/** Parse a raw `?risk=` value, accepting only the three band-bound values. */
export const parseRisk = (raw: string | null): RiskBand | undefined =>
  raw && RISK_BANDS.includes(raw as RiskBand) ? (raw as RiskBand) : undefined;

const FLOC_PARAM = "floc";
const FLOC_TOTAL_PARAM = "floc_total";
const CONDITION_PARAM = "condition";

/** The most FLOCs a condition list carries. Each rides the page URL as
 *  `floc=` and the lookup GET as `ids=`; a few hundred would pass the usual
 *  8 KB request-line limit (the lookup fails, the URL can't be reloaded or
 *  shared). Past this the list shows the first N and says how many it names. */
export const MAX_CONDITION_FLOCS = 100;

/** A node insight's condition as a flat FLOC list: the FLOCs it names and
 *  the condition's label, on the scope's URL. */
export interface ConditionReport {
  condition: string;
  flocs: string[];
  /** How many FLOCs the condition names, when more than `flocs` carries. */
  total?: number;
  /** The condition is one of the drilled-into node's issue groups: open the
   *  node's own location page with that issue focused, not a flat list. */
  issue?: boolean;
}

const ISSUE_PARAM = "issue";

/** Focus an issue group (by its label) on a location tier URL, preserving
 *  every other param. */
export const withIssue = (url: string, label: string): string =>
  mutateSearch(url, (params) => {
    params.set(ISSUE_PARAM, label);
  });

/** The issue group a URL focuses, or undefined. */
export const parseIssue = (sp: URLSearchParams): string | undefined =>
  sp.get(ISSUE_PARAM) || undefined;

/** Open the condition list on a URL, replacing any prior one and any risk
 *  band (the two flat views are exclusive). Carries at most
 *  `MAX_CONDITION_FLOCS`, and the full count when it names more. */
export const withCondition = (url: string, report: ConditionReport): string =>
  mutateSearch(url, (params) => {
    params.delete(RISK_PARAM);
    params.delete(FLOC_PARAM);
    params.delete(FLOC_TOTAL_PARAM);
    params.set(CONDITION_PARAM, report.condition);
    const total = Math.max(report.total ?? 0, report.flocs.length);
    report.flocs
      .slice(0, MAX_CONDITION_FLOCS)
      .forEach((floc) => params.append(FLOC_PARAM, floc));
    if (total > MAX_CONDITION_FLOCS) {
      params.set(FLOC_TOTAL_PARAM, String(total));
    }
  });

/** Close the condition list, preserving every other param. */
export const withoutCondition = (url: string): string =>
  mutateSearch(url, (params) => {
    params.delete(FLOC_PARAM);
    params.delete(FLOC_TOTAL_PARAM);
    params.delete(CONDITION_PARAM);
  });

/** The condition list a URL carries, or undefined when it names no FLOC. */
export const parseCondition = (
  sp: URLSearchParams,
): ConditionReport | undefined => {
  const flocs = sp.getAll(FLOC_PARAM).filter(Boolean);
  if (flocs.length === 0) return undefined;
  const total = Number(sp.get(FLOC_TOTAL_PARAM));
  return {
    condition: sp.get(CONDITION_PARAM) || "",
    flocs,
    ...(Number.isInteger(total) && total > flocs.length ? { total } : {}),
  };
};

const COMPONENT_TYPE_PARAM = "component_type";

/**
 * Pin (or, with undefined, clear) the hierarchy's component-type filter on a
 * URL, preserving every other param. One mutator for both directions so
 * carrying the filter across a drill and clearing it are the same call.
 */
export const withComponentType = (
  url: string,
  componentType: string | undefined,
): string =>
  mutateSearch(url, (params) => {
    if (componentType) params.set(COMPONENT_TYPE_PARAM, componentType);
    else params.delete(COMPONENT_TYPE_PARAM);
  });

/** Read `?component_type=`; any non-empty value is a valid type (the options
 *  are plant data, not a fixed vocabulary). */
export const parseComponentType = (raw: string | null): string | undefined =>
  raw ? raw : undefined;

const ATTRIBUTE_COLUMN_PARAM = "attribute_column";
const ATTRIBUTE_VALUE_PARAM = "attribute_value";

/** One picked (column, value) pair from the locations dataset. */
export interface AttributeFilterValue {
  column: string;
  value: string;
}

/**
 * Canonical form of a selection: blank halves dropped, repeated pairs
 * deduplicated, and grouped by column — columns in first-picked order, values
 * in picked order within a column. The URL, the API and the query keys all
 * carry this form, so the same selection always reads, sends and caches alike.
 */
export const normalizeAttributeFilters = (
  pairs: readonly AttributeFilterValue[] | undefined,
): AttributeFilterValue[] => {
  const byColumn = new Map<string, string[]>();
  for (const { column, value } of pairs ?? []) {
    if (!column || !value) continue;
    const values = byColumn.get(column) ?? [];
    if (!values.includes(value)) values.push(value);
    byColumn.set(column, values);
  }
  return [...byColumn].flatMap(([column, values]) =>
    values.map((value) => ({ column, value })),
  );
};

/**
 * The attribute selection as the API's repeated, position-paired query params.
 * Empty when nothing is picked, so no param is sent at all.
 */
export const attributeQueryParams = (
  attributeFilters: readonly AttributeFilterValue[] | undefined,
): { attribute_column?: string[]; attribute_value?: string[] } => {
  const pairs = normalizeAttributeFilters(attributeFilters);
  return pairs.length
    ? {
        attribute_column: pairs.map((p) => p.column),
        attribute_value: pairs.map((p) => p.value),
      }
    : {};
};

/** Axios' default writes arrays as ``k[]=a&k[]=b``; the API wants plain
 *  repeats (``k=a&k=b``). */
export const REPEATED_PARAMS_SERIALIZER = { indexes: null } as const;

/**
 * Set the hierarchy's location-attribute selection on a URL (an empty or
 * undefined list clears it), preserving every other param. Written as repeated
 * `attribute_column` / `attribute_value` pairs, matched by position — the same
 * shape the API takes — so a single pair is the old single-pair URL.
 */
export const withAttributeFilters = (
  url: string,
  attributes: readonly AttributeFilterValue[] | undefined,
): string =>
  mutateSearch(url, (params) => {
    params.delete(ATTRIBUTE_COLUMN_PARAM);
    params.delete(ATTRIBUTE_VALUE_PARAM);
    for (const { column, value } of normalizeAttributeFilters(attributes)) {
      params.append(ATTRIBUTE_COLUMN_PARAM, column);
      params.append(ATTRIBUTE_VALUE_PARAM, value);
    }
  });

/** Read the repeated `?attribute_column=&attribute_value=` pairs. Pairs match
 *  by position; a blank half, or a column or value with no partner (a stale or
 *  hand-edited URL), is dropped rather than shifting the pairs after it.
 *  Repeats are deduplicated. Any non-empty pair is valid — the columns and
 *  values are plant data, not a fixed vocabulary. */
export const parseAttributeFilters = (
  sp: URLSearchParams,
): AttributeFilterValue[] => {
  const columns = sp.getAll(ATTRIBUTE_COLUMN_PARAM);
  const values = sp.getAll(ATTRIBUTE_VALUE_PARAM);
  const pairs: AttributeFilterValue[] = [];
  for (let i = 0; i < Math.min(columns.length, values.length); i++) {
    pairs.push({ column: columns[i], value: values[i] });
  }
  return normalizeAttributeFilters(pairs);
};

/** Toggle one (column, value) in a selection: remove it if picked, else add it
 *  after the values already picked. */
export const toggleAttributeFilter = (
  current: readonly AttributeFilterValue[],
  pair: AttributeFilterValue,
): AttributeFilterValue[] =>
  current.some((p) => p.column === pair.column && p.value === pair.value)
    ? current.filter(
        (p) => !(p.column === pair.column && p.value === pair.value),
      )
    : normalizeAttributeFilters([...current, pair]);

/**
 * An order-insensitive key for a selection's picks outside one column. A
 * column's options are narrowed by exactly these picks, so two selections with
 * the same key offer that column the same options.
 */
export const picksOutsideColumnKey = (
  pairs: readonly AttributeFilterValue[],
  column: string,
): string =>
  JSON.stringify(
    pairs
      .filter((p) => p.column !== column)
      .map((p) => [p.column, p.value])
      .sort(),
  );

const NEW_CHAT_PARAM = "chat";
const NEW_CHAT_VALUE = "new";

/** True when the URL carries the one-shot "open the chat panel empty" signal. */
export const parseNewChat = (raw: string | null): boolean =>
  raw === NEW_CHAT_VALUE;

/** Set the new-chat signal on a URL, preserving every other param. */
export const withNewChat = (url: string): string =>
  mutateSearch(url, (params) => params.set(NEW_CHAT_PARAM, NEW_CHAT_VALUE));

/** Strip the new-chat signal from a URL, preserving every other param. */
export const withoutNewChat = (url: string): string =>
  mutateSearch(url, (params) => params.delete(NEW_CHAT_PARAM));

/**
 * URL for "start a new chat" from a scoped recents page. Neither an asset nor
 * a location auto-opens its chat panel on arrival, so both carry the
 * `chat=new` signal to open the panel empty. Neither shape carries a chat_id —
 * the dialogue is minted lazily on the first message. (#24194)
 */
export const newChatUrl = ({
  afElementId,
  locationId,
}: {
  afElementId?: string | null;
  locationId?: string | null;
}): string => {
  if (afElementId) return withNewChat(assetUrl(afElementId));
  if (locationId) return withNewChat(locationUrl(locationId));
  return EQUIPMENT_AI_BASE;
};

/**
 * Asset (monitored component) URL: the AF element id rides `?asset=`, with the
 * hierarchy position it was reached from carried alongside so the breadcrumb
 * renders without an extra fetch.
 */
export const assetUrl = (
  afElementId: string,
  opts: LocationOption = {},
): string =>
  hierarchyUrl(
    {
      level: opts.parents?.parentAf ? "af" : "system",
      parentSite: opts.parents?.parentSite,
      parentUnit: opts.parents?.parentUnit,
      parentSystem: opts.parents?.parentSystem,
      parentAf: opts.parents?.parentAf,
      assetId: afElementId,
    },
    { chatId: opts.chatId },
  );

const applyChatParam = (params: URLSearchParams, chatId?: number): void => {
  if (chatId !== undefined) params.set("chat_id", String(chatId));
};

const applyParentParams = (
  params: URLSearchParams,
  parents?: LocationParents,
): void => {
  if (!parents) return;
  if (parents.parentSite) params.set("site", parents.parentSite);
  if (parents.parentUnit) params.set("unit", parents.parentUnit);
  if (parents.parentSystem) params.set("system", parents.parentSystem);
  if (parents.parentAf) params.set("af", parents.parentAf);
};

const withSearch = (path: string, params: URLSearchParams): string => {
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

const mutateSearch = (
  url: string,
  mutate: (params: URLSearchParams) => void,
): string => {
  const [path, search = ""] = url.split("?");
  const params = new URLSearchParams(search);
  mutate(params);
  return withSearch(path, params);
};
